import React, { useState, useEffect } from 'react';
import { 
  X, 
  Package, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertCircle,
  Search,
  RefreshCw,
  Eye
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { AnalyticsService, MovementCategoryItem } from '../services/analyticsService';

interface MovementCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: 'no_movement' | 'slow_moving' | 'medium_moving' | 'fast_moving';
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

const MovementCategoryModal: React.FC<MovementCategoryModalProps> = ({
  isOpen,
  onClose,
  category,
  dateRange
}) => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  
  const [items, setItems] = useState<MovementCategoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get category info
  const categoryInfo = AnalyticsService.getMovementCategoryInfo(category);

  // Load items for the category
  const loadItems = async () => {
    if (!user?.id || !currentStore?.id || !isOpen) return;

    try {
      setLoading(true);
      setError(null);
      
      const defaultRange = AnalyticsService.getDefaultDateRange();
      const data = await AnalyticsService.getItemsByMovementCategory(
        user.id,
        category,
        currentStore.id,
        dateRange?.startDate || defaultRange.startDate,
        dateRange?.endDate || defaultRange.endDate,
        100, // Load up to 100 items
        0
      );
      
      setItems(data.items || []);
    } catch (err) {
      console.error('Error loading category items:', err);
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  // Load items when modal opens or dependencies change
  useEffect(() => {
    if (isOpen) {
      loadItems();
    }
  }, [isOpen, user?.id, currentStore?.id, category, dateRange]);

  // Filter items based on search term
  const filteredItems = items.filter(item =>
    item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get category icon
  const getCategoryIcon = () => {
    switch (category) {
      case 'no_movement':
        return AlertCircle;
      case 'slow_moving':
        return TrendingDown;
      case 'medium_moving':
        return Activity;
      case 'fast_moving':
        return TrendingUp;
      default:
        return Package;
    }
  };

  const CategoryIcon = getCategoryIcon();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-gray-200 ${categoryInfo.bgColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 ${categoryInfo.color} rounded-lg`}>
                <CategoryIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {categoryInfo.name} Items
                </h2>
                <p className="text-sm text-gray-600">
                  {categoryInfo.description}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items by name, SKU, barcode, category, or brand..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={loadItems}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-600">Loading items...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Items</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={loadItems}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No matching items found' : `No ${categoryInfo.name.toLowerCase()} items`}
              </h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : `There are currently no items in the ${categoryInfo.name.toLowerCase()} category.`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Results Summary */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {filteredItems.length} of {items.length} items
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>
              </div>

              {/* Items Grid */}
              <div className="grid gap-4">
                {filteredItems.map((item) => (
                  <div
                    key={item.item_id}
                    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start space-x-4">
                      {/* Item Image */}
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                        {item.primary_image_url ? (
                          <img
                            src={item.primary_image_url}
                            alt={item.item_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {item.item_name}
                            </h4>
                            <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                              <span>SKU: {item.sku}</span>
                              {item.barcode && <span>Barcode: {item.barcode}</span>}
                              {item.category && <span>Category: {item.category}</span>}
                              {item.brand && <span>Brand: {item.brand}</span>}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              Stock: {item.current_stock}
                            </div>
                            <div className="text-xs text-gray-500">
                              Value: MVR {item.total_value?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                        </div>

                        {/* Movement Stats */}
                        <div className="mt-3 flex items-center space-x-6 text-xs text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Activity className="w-3 h-3" />
                            <span>{item.total_movements} movements</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Package className="w-3 h-3" />
                            <span>{item.total_quantity_moved} qty moved</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              Last: {item.last_movement_date === '1900-01-01' 
                                ? 'Never' 
                                : new Date(item.last_movement_date).toLocaleDateString()
                              }
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <TrendingUp className="w-3 h-3" />
                            <span>{item.avg_daily_movement} avg/day</span>
                          </div>
                        </div>

                        {/* Days since last movement */}
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.days_since_last_movement > 60
                              ? 'bg-red-100 text-red-800'
                              : item.days_since_last_movement > 30
                              ? 'bg-orange-100 text-orange-800'
                              : item.days_since_last_movement > 14
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {item.days_since_last_movement === 0 
                              ? 'Moved today'
                              : `${item.days_since_last_movement} days since last movement`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Data from {dateRange ? new Date(dateRange.startDate).toLocaleDateString() : 'last 90 days'} to {dateRange ? new Date(dateRange.endDate).toLocaleDateString() : 'today'}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovementCategoryModal;
